import { ReactNode, useState } from "react";
import Image from "~/components/atoms/Image";
import {
    BoardingSchool,
    PremierePartners,
    DevelopmentalCoaching,
    MembershipTypes,
    CollegeRecruiting,
    OtherExperiences,
    SportCamps,
} from "~/components/organisms/navigation-bar/sections";
import { SubSectionContainer } from "~/components/organisms/navigation-bar/shared";
import plusLogo from "~/assets/imga-plus-text-logo.svg";
import CaretTriangle from "~/assets/caret-triangle-down.svg";
import { matchPath, NavLink, useLocation } from "react-router";
import { cn, MOCK_ATHLETE_ID, MOCK_ATHLETE_SPORT_ID } from "~/lib/utils";

function DesktopContainer({ children }: { children?: ReactNode }) {
    return <div className="max-w-[1290px] p-4">{children}</div>;
}

function HoverMenuItem({ children, text }: { children?: ReactNode; text: string }) {
    const [isOpen, setIsOpen] = useState(false);
    const handleHover = () => setIsOpen(true);
    const handleLeave = () => setIsOpen(false);

    return (
        <div
            className="group"
            role="button"
            tabIndex={0}
            onMouseEnter={handleHover}
            onMouseLeave={handleLeave}
        >
            <div
                className={`flex h-[60px] cursor-pointer flex-row items-center gap-1 p-2 px-[23px] group-hover:mb-[-2px] group-hover:border-b-2 group-hover:border-b-blue-80`}
            >
                <span className="font-bold text-blue-80">{text}</span>
                <Image
                    src={CaretTriangle}
                    alt="Menu Dropdown Caret"
                    className="z-0 mt-1 h-full w-[12px] transition-all"
                />
            </div>
            <div
                className={`absolute left-1/2 top-full z-[60] mt-px hidden max-w-[1290px] -translate-x-1/2 cursor-default bg-white px-1 shadow-2xl group-hover:block sm:w-full xl:w-[1290px]`}
            >
                {children}
            </div>
            {text === "On Campus" && isOpen && (
                <div
                    className={`absolute inset-0 left-64 right-60 before:absolute before:left-64 before:right-60 before:bg-transparent before:content-[''] group-hover:before:block`}
                ></div>
            )}
        </div>
    );
}

function OnCampus() {
    return (
        <DesktopContainer>
            <div className="flex flex-wrap gap-8">
                <SubSectionContainer title="Youth">
                    <div className="flex flex-wrap gap-8">
                        <div className="flex-auto">
                            <BoardingSchool />
                        </div>
                        <div className="flex-auto">
                            <SportCamps />
                        </div>
                    </div>
                </SubSectionContainer>
                <OtherExperiences />
                <div className="flex max-w-[250px] flex-auto flex-col gap-4">
                    <SubSectionContainer title="Premier Partners">
                        <div className="py-4">
                            <PremierePartners />
                        </div>
                    </SubSectionContainer>
                </div>
            </div>
        </DesktopContainer>
    );
}

function Online() {
    return (
        <DesktopContainer>
            <Image loading="lazy" alt="IMGA+ Logo" src={plusLogo} className="w-[300px] py-4" />
            <div className="flex flex-wrap justify-between gap-8">
                <div className="flex flex-col gap-8">
                    <DevelopmentalCoaching />
                    <MembershipTypes />
                </div>
                <CollegeRecruiting tripleColumns />
                <div className="flex max-w-[250px] flex-auto flex-col gap-4">
                    <SubSectionContainer title="Premier Partners">
                        <div className="py-4">
                            <PremierePartners />
                        </div>
                    </SubSectionContainer>
                </div>
            </div>
        </DesktopContainer>
    );
}

export function NavigationDesktop({ isAuthenticated }: { isAuthenticated?: boolean }) {
    const location = useLocation();
    const linkStyles = "flex flex-col h-full items-center justify-center menu-link";
    const linkTextStyles = "text-normal text-blue-80";
    const activeLinkStyles = "active-bottom font-bold";

    if (isAuthenticated) {
        return (
            <nav className="relative flex w-full flex-1 items-stretch justify-around gap-8">
                <NavLink
                    to="/college-recruiting/colleges/football"
                    className={() =>
                        cn(
                            matchPath("/college-recruiting/colleges/*", location.pathname)
                                ? activeLinkStyles
                                : "",
                            linkStyles,
                        )
                    }
                >
                    <span className={linkTextStyles}>Colleges</span>
                </NavLink>
                <NavLink
                    to={`/profile/${MOCK_ATHLETE_ID}/${MOCK_ATHLETE_SPORT_ID}`}
                    className={({ isActive }) => cn(isActive ? activeLinkStyles : "", linkStyles)}
                >
                    <span className={linkTextStyles}>Profile</span>
                </NavLink>
                <NavLink
                    to="/resources"
                    className={({ isActive }) => cn(isActive ? activeLinkStyles : "", linkStyles)}
                >
                    <span className={linkTextStyles}>Resources</span>
                </NavLink>
            </nav>
        );
    }

    return (
        <span className="flex flex-row items-center">
            <HoverMenuItem text="On Campus">
                <OnCampus />
            </HoverMenuItem>
            <HoverMenuItem text="Online">
                <Online />
            </HoverMenuItem>
            <a
                className="flex cursor-pointer flex-row items-center gap-1 p-2 px-[23px] font-bold text-blue-80"
                href="https://www.imgacademy.com/contact-us"
            >
                Contact Us
            </a>
        </span>
    );
}
